import { forwardRef } from "react";

import Box from "@mui/material/Box";
import { Theme } from "@mui/material/styles";

import { TicketingPrizeOutput } from "../../types";
import { PrizeImage } from "../PrizeImage/PrizeImage";

import { mergeSx } from "@/common/helpers/mergeSx";
import { unstyledButtonSx } from "@/common/helpers/unstyledButtonSx";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormTypography } from "@/forms-design-system/FormTypography";

const IMAGE_SIZE = 56;

interface PrizeThumbnailProps {
  prize: TicketingPrizeOutput;
  index: number;
  isSelected: boolean;
  onClick: () => void;
}

export const PrizeThumbnail = forwardRef<HTMLButtonElement, PrizeThumbnailProps>(
  ({ prize, index, isSelected, onClick }, ref) => {
    const { t } = useTranslate();

    return (
      <Box
        component="button"
        type="button"
        ref={ref}
        onClick={onClick}
        aria-label={t("ticketing", "prizes.selectPrize", { number: index + 1, title: prize.title })}
        aria-pressed={isSelected}
        data-test="prize-thumbnail"
        sx={mergeSx(unstyledButtonSx, (theme: Theme) => ({
          flexShrink: 0,
          padding: 0.25,
          borderRadius: 1.5,
          border: `1px solid ${isSelected ? theme.palette.formsComponents.divider.borderColor : "transparent"}`,
          backgroundColor: isSelected ? theme.palette.forms.surface.quiet : "transparent",
        }))}
      >
        <Box sx={{ opacity: isSelected ? 1 : 0.5, display: "flex" }}>
          {prize.photoUrl ? (
            <PrizeImage photoUrl={prize.photoUrl} alt="" sx={{ width: IMAGE_SIZE, height: IMAGE_SIZE }} />
          ) : (
            <Box
              sx={(theme) => ({
                width: IMAGE_SIZE,
                height: IMAGE_SIZE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: theme.radius(1),
                backgroundColor: theme.palette.forms.surface.reverse,
              })}
            >
              <FormTypography
                variant="subtitle2"
                vibe="intense"
                sx={(theme) => ({ lineHeight: "16px", textAlign: "center", maxWidth: theme.spacing(5) })}
              >
                {t("ticketing", "prizes.fallbackLabel", { number: index + 1 })}
              </FormTypography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

PrizeThumbnail.displayName = "PrizeThumbnail";
